import { Field, Flex, Input, List, Text } from '@chakra-ui/react';
import { Control, FieldValues, useController } from 'react-hook-form';
import { BaseButton, BaseIcon, BasePopover, BaseTooltip } from '@invara-dev/react-base';
import { faPlus, faTrash, faUsers } from '@fortawesome/free-solid-svg-icons';

type GuestTravellerNamesPopoverInputProps = {
  name: string;
  control: Control<FieldValues>;
  readOnly?: boolean;
};
// Edit Activity implementation: popover-based editor for guest traveller names.
// - Compact button in the form row
// - Tooltip icon that shows the current list of guest travellers on hover
// - Popover content allows editing the underlying string[] value
//
// NOTE: This intentionally does NOT enforce rate capacity, as that logic
// is handled in the itinerary wizard flow.
const GuestTravellerNamesPopoverInput = (props: GuestTravellerNamesPopoverInputProps) => {
  const { field } = useController({
    name: props.name,
    control: props.control
  });

  const currentValue = (field.value as string[] | undefined) ?? [];
  const count = currentValue.length;

  const handleAddGuest = () => {
    if (props.readOnly) {
      return;
    }
    const nextIndex = currentValue.length + 1;
    const newArray = [...currentValue, `Guest ${nextIndex}`];
    field.onChange(newArray);
  };

  const handleRemoveGuest = (index: number) => {
    if (props.readOnly) {
      return;
    }
    const newArray = [...currentValue];
    newArray.splice(index, 1);
    field.onChange(newArray);
  };

  const handleGuestNameChange = (index: number, value: string) => {
    const newArray = [...currentValue];
    newArray[index] = value;
    field.onChange(newArray);
  };

  const tooltipContent = (
    <Flex direction='column' gap='8px'>
      <List.Root>
        {currentValue.map((name, index) => (
          <List.Item key={index}>
            <Text fontSize='sm'>{name}</Text>
          </List.Item>
        ))}
      </List.Root>
      <Text fontSize='xs' color='gray.400'>
        These names are populated from OBT if available, otherwise as Guest 1, Guest 2, etc.
      </Text>
    </Flex>
  );

  return (
    <Field.Root>
      <Field.Label>Guest Travelers</Field.Label>
      <Flex gap='8px' alignItems='center'>
        <BasePopover
          trigger={
            <BaseButton size='sm' variant='outline' disabled={props.readOnly}>
              {count === 0 ? 'Add guests' : `Edit guests (${count})`}
            </BaseButton>
          }>
          <Flex direction='column' gap='8px'>
            {currentValue.map((name, index) => (
              <Flex key={index} gap='8px' alignItems='center'>
                <Input
                  value={name}
                  onChange={(e) => handleGuestNameChange(index, e.target.value)}
                  placeholder={`Guest ${index + 1}`}
                  readOnly={props.readOnly}
                />
                {!props.readOnly && (
                  <BaseIcon
                    icon={faTrash}
                    toolTip='Remove guest traveler'
                    cursor='pointer'
                    onClick={() => handleRemoveGuest(index)}
                  />
                )}
              </Flex>
            ))}

            {!props.readOnly && (
              <BaseButton size='xs' variant='outline' icon={faPlus} onClick={handleAddGuest}>
                Add Guest
              </BaseButton>
            )}
          </Flex>
        </BasePopover>

        {count > 0 && (
          <BaseTooltip content={tooltipContent}>
            <BaseIcon icon={faUsers} nonInteractive color='gray.600' />
          </BaseTooltip>
        )}
      </Flex>
    </Field.Root>
  );
};

/**
 *
 * Renders an inline editable list of guest names backed by a string[] value,
 * with capacity enforcement handled inside the GuestTravellerNamesInput.
 *
 * NOTE: This must be rendered directly using .render(), NOT through renderFieldHelperInput.
 */
export const EditActivityGuestTravellerNamesField = <T extends FieldValues>(
  control: Control<T>,
  fieldName: keyof T & string,
  readOnly?: boolean
) => {
  return {
    fieldName,
    render: () => (
      <GuestTravellerNamesPopoverInput name={fieldName} control={control as Control<FieldValues>} readOnly={readOnly} />
    )
  };
};
